import { DataTable, useContainerHeight } from '@components/DataTable';
import { DataTableColumn, SelectionModel, SortModel } from '@components/DataTable/types';
import { Box, Skeleton, TableCell, TableRow, Typography } from '@mui/material';
import ProLogoWithPlan from './components/ProLogoWithPlan';
import React, { useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export interface Company {
  _id: string;
  logo?: string;
  photo?: string;
  commercialName?: string;
  email?: string;
  bossFirstname?: string;
  bossLastname?: string;
  bossEmail?: string;
  address?: string;
  address2?: string;
  postal?: string;
  city?: string;
  country?: string;
  isFeatured?: boolean;
  userSubscriptionPlans?: Array<{
    name?: string;
    status?: string;
  }>;
  totalJobOffers?: number;
  totalJobApplications?: number;
  totalBrands?: number;
}

export interface CompanyDataTableProps {
  companies: Company[];
  totalDocs: number;
  loading: boolean;
  isLoadingMore?: boolean;
  page: number;
  pageSize: number;
  selectionModel: SelectionModel;
  sortModel: SortModel[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onSelectionModelChange: (model: SelectionModel) => void;
  onSortModelChange: (model: SortModel[]) => void;
  onScrollEnd?: () => void;
}

const CompanyDataTable = ({
  companies,
  totalDocs,
  loading,
  isLoadingMore,
  page,
  pageSize,
  selectionModel,
  sortModel,
  onPageChange,
  onPageSizeChange,
  onSelectionModelChange,
  onSortModelChange,
  onScrollEnd,
}: CompanyDataTableProps) => {
  const navigate = useNavigate();
  const { containerHeight, containerRef } = useContainerHeight();

  const getBossFullName = useCallback((company: Company) => {
    const fullName = [company.bossFirstname, company.bossLastname].filter(Boolean).join(' ').trim();
    return fullName || '-';
  }, []);

  const getActivePlanName = useCallback((company: Company) => {
    const activePlan = company.userSubscriptionPlans?.find(
      plan => plan.status?.toLowerCase() === 'active',
    );
    return activePlan?.name;
  }, []);

  const columns = useMemo<DataTableColumn<Company>[]>(() => [
    {
      id: 'commercialName',
      label: 'Nom établissement',
      minWidth: 340,
      sortable: true,
      sticky: true,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
          <ProLogoWithPlan
            src={row.logo || row.photo || undefined}
            alt={row.commercialName || 'Company'}
            fallbackLetter={row.commercialName?.[0]?.toUpperCase() || 'C'}
            planName={getActivePlanName(row)}
          />
          <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', minWidth: 0 }}>
            <Typography
              variant="h5"
              sx={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flex: 1,
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}>
              {row.commercialName || '-'}
            </Typography>
            <Typography
              fontSize="13px"
              color="text.secondary"
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {row.email || '-'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'bossFullName',
      label: 'Boss nom complet',
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Typography
            variant="h5"
            sx={{
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: 1,
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}>
            {getBossFullName(row)}
          </Typography>
          <Typography
            fontSize="13px"
            color="text.secondary"
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.bossEmail || '-'}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'totalJobOffers',
      label: 'Total offres',
      minWidth: 150,
      align: 'center',
      sortable: true,
      renderCell: ({ row }) => (
        <Typography
          component={Link}
          to={`/companies/${row._id}/job-offers`}
          onClick={(e) => e.stopPropagation()}
          sx={{ fontWeight: 500, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          {row.totalJobOffers ?? 0}
        </Typography>
      ),
    },
    {
      id: 'totalJobApplications',
      label: 'Total candidatures',
      minWidth: 150,
      align: 'center',
      sortable: true,
      renderCell: ({ row }) => (
        <Typography
          component={Link}
          to={`/companies/${row._id}/applications`}
          onClick={(e) => e.stopPropagation()}
          sx={{ fontWeight: 500, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          {row.totalJobApplications ?? 0}
        </Typography>
      ),
    },
    {
      id: 'totalBrands',
      label: 'Total marques',
      minWidth: 150,
      align: 'center',
      sortable: true,
      renderCell: ({ row }) => (
        <Typography
          component={Link}
          to={`/companies/${row._id}/brands`}
          onClick={(e) => e.stopPropagation()}
          sx={{ fontWeight: 500, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          {row.totalBrands ?? 0}
        </Typography>
      ),
    },
    {
      id: 'isFeatured',
      label: 'En vedette',
      align: 'center',
      minWidth: 100,
      renderCell: ({ row }) => (row.isFeatured ? 'Oui' : 'Non'),
    },
  ], [getActivePlanName, getBossFullName]);

  const rowSkeleton = useMemo(() => (
    <TableRow>
      {columns.map((column, columnIndex) => (
        <TableCell
          key={`company-row-skeleton-${column.id}`}
          align={column.align || 'left'}
          sx={{ minWidth: column.minWidth }}
        >
          {columnIndex === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
              <Skeleton variant="circular" width={34} height={34} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0, flex: 1 }}>
                <Skeleton variant="text" width="65%" height={18} />
                <Skeleton variant="text" width="45%" height={16} />
              </Box>
            </Box>
          ) : (
            <Skeleton variant="text" width={column.align === 'center' ? 36 : '70%'} height={18} sx={{ mx: column.align === 'center' ? 'auto' : 0 }} />
          )}
        </TableCell>
      ))}
    </TableRow>
  ), [columns]);

  return (
    <Box sx={{ width: '100%', flex: 1 }} ref={containerRef}>
      <DataTable
        rows={companies}
        columns={columns}
        loading={loading}
        isLoadingMore={isLoadingMore}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        checkboxSelection
        selectionModel={selectionModel}
        onSelectionModelChange={onSelectionModelChange}
        pagination={{
          currentPage: page + 1,
          pageSize,
          totalRows: totalDocs,
          pageSizeOptions: [10, 20, 50, 100],
          onPageChange: (newPage: number) => onPageChange(newPage - 1),
          onPageSizeChange,
        }}
        height={containerHeight}
        stickyHeader
        onScrollEnd={onScrollEnd}
        onRowClick={row => navigate(`/companies/${row._id}/edit`)}
        rowSkeleton={rowSkeleton}
      />
    </Box>
  );
};

export default CompanyDataTable;
